"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Icon, Input, LocateFixed, Search, Select } from "@/components";
import type { MapSpec, Point, PointSet } from "@/lib/map";
import type { MapView } from "../../(editor)/map/MapCanvas";

export interface LocatorProps {
  pointSet: PointSet;
  spec: MapSpec;
  /** Live map view (the distance anchor is the map center). */
  view: MapView | null;
  onSelect: (point: Point) => void;
  /** Move the map to a new anchor (the "use my location" path). */
  onAnchor: (center: [number, number]) => void;
  selectedId: string | null;
  /** The map region (rendered by ViewerSurface) — slotted into the grid. */
  children: ReactNode;
}

const RADII = [5, 10, 25, 50, 100];
const PAGE = 8;

/** Great-circle distance in miles. */
function miles(a: [number, number], b: [number, number]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * 3958.8 * Math.asin(Math.min(1, Math.sqrt(s)));
}

function formatMiles(d: number): string {
  return d < 10 ? `${d.toFixed(1)} mi` : `${Math.round(d)} mi`;
}

interface LocatorRow {
  point: Point;
  distance: number;
}

/**
 * Store-locator embed preset (M6 §07): a search + radius bar over a results
 * list glued to the map. The distance anchor is the live map center — pan the
 * map (or hit "Use my location") and the list re-ranks; no address geocoding
 * happens in the viewer (the margin guardrail).
 */
export function Locator({ pointSet, spec, view, onSelect, onAnchor, selectedId, children }: LocatorProps) {
  const [query, setQuery] = useState("");
  const [radius, setRadius] = useState(25);
  const [shown, setShown] = useState(PAGE);

  const titleCol = spec.popup.titleColumn ?? pointSet.columns[0]?.name;
  const subCol = spec.popup.fields.find((f) => f !== titleCol) ?? pointSet.columns.find((c) => c.name !== titleCol)?.name;

  const anchor = useMemo<[number, number]>(() => {
    if (view) return view.center;
    const b = pointSet.bounds;
    if (b) return [(b.minLng + b.maxLng) / 2, (b.minLat + b.maxLat) / 2];
    return spec.view.center;
  }, [view, pointSet, spec]);

  const rows = useMemo<LocatorRow[]>(() => {
    const needle = query.trim().toLowerCase();
    return pointSet.points
      .filter((p) => {
        if (needle === "") return true;
        if (titleCol && (p.props[titleCol] ?? "").toLowerCase().includes(needle)) return true;
        return Object.values(p.props).some((v) => v.toLowerCase().includes(needle));
      })
      .map((point) => ({ point, distance: miles(anchor, [point.lng, point.lat]) }))
      .filter((r) => r.distance <= radius)
      .sort((a, b) => a.distance - b.distance);
  }, [pointSet, query, titleCol, anchor, radius]);

  const visible = rows.slice(0, shown);

  function locateMe() {
    navigator.geolocation?.getCurrentPosition(
      (pos) => onAnchor([pos.coords.longitude, pos.coords.latitude]),
      () => {
        /* denied/unavailable — the anchor stays the map center */
      },
    );
  }

  return (
    <div className="mv-locator">
      <div className="mv-locator__bar">
        <span className="mv-locator__searchwrap">
          <Icon icon={Search} size="sm" className="mv-locator__glyph" />
          <Input
            type="search"
            className="mv-locator__search"
            placeholder="Search locations…"
            aria-label="Search locations"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShown(PAGE);
            }}
          />
        </span>
        <label className="mv-locator__radius">
          <span className="mv-locator__radiuslabel">Radius</span>
          <Select
            aria-label="Search radius"
            value={String(radius)}
            onChange={(e) => {
              setRadius(Number(e.target.value));
              setShown(PAGE);
            }}
          >
            {RADII.map((r) => (
              <option key={r} value={r}>
                {r} mi
              </option>
            ))}
          </Select>
        </label>
        <button type="button" className="mv-locator__locate" onClick={locateMe}>
          <Icon icon={LocateFixed} size="sm" />
          Use my location
        </button>
      </div>

      <div className="mv-locator__grid">
        <div className="mv-locator__list" role="list" aria-label="Nearby locations">
          <p className="mv-locator__count">
            {rows.length.toLocaleString()} {rows.length === 1 ? "location" : "locations"} within {radius} mi
          </p>
          {visible.map((row, index) => {
            const title = titleCol ? row.point.props[titleCol] : "";
            const sub = subCol ? row.point.props[subCol] : "";
            const selected = row.point.id === selectedId;
            return (
              <button
                key={row.point.id}
                type="button"
                role="listitem"
                className={selected ? "mv-locrow mv-locrow--selected" : "mv-locrow"}
                onClick={() => onSelect(row.point)}
              >
                <span className={index === 0 ? "mv-locrow__num mv-locrow__num--answer" : "mv-locrow__num"}>
                  {index + 1}
                </span>
                <span className="mv-locrow__body">
                  <span className="mv-locrow__name">{title !== "" ? title : "Location"}</span>
                  {sub && <span className="mv-locrow__sub">{sub}</span>}
                </span>
                <span className="mv-locrow__dist">{formatMiles(row.distance)}</span>
              </button>
            );
          })}
          {rows.length === 0 && <p className="mv-locator__empty">Nothing within {radius} mi — widen the radius or pan the map.</p>}
          {rows.length > shown && (
            <button type="button" className="mv-locator__more" onClick={() => setShown((s) => s + PAGE)}>
              Show {Math.min(PAGE, rows.length - shown)} more
            </button>
          )}
        </div>
        <div className="mv-locator__map">{children}</div>
      </div>
    </div>
  );
}
